/**
 * Passwords that meet the length rule but are still among the first things an attacker tries.
 *
 * This is not a breach database (that needs a large list or a network call to a service); it refuses
 * the families of passwords that account for most guessing: common words with digits or look-alike
 * characters added, keyboard and alphabet runs, one or two characters repeated, and a password built
 * from the person's own name or e-mail address.
 */
const COMMON_WORDS = [
    'password',
    'passwort',
    'welcome',
    'qwerty',
    'qwertyuiop',
    'asdfghjkl',
    'zxcvbnm',
    'letmein',
    'admin',
    'administrator',
    'iloveyou',
    'monkey',
    'dragon',
    'football',
    'baseball',
    'basketball',
    'master',
    'sunshine',
    'princess',
    'login',
    'trustno',
    'shadow',
    'superman',
    'batman',
    'school',
    'teacher',
    'student',
    'principal',
    'changeme',
    'default',
    'secret',
    'summer',
    'winter',
    'spring',
    'autumn',
    'hello',
    'freedom',
    'whatever',
    'starwars',
    'computer',
    'internet',
    'testing',
    'test',
    'guest',
    'root',
    'user',
    'access',
    'abcdef',
    'abcd',
    'number',
    'love',
    'family',
    'friend',
    'mother',
    'father',
    'killer',
    'jordan',
    'michael',
    'jennifer',
    'soccer',
    'hockey',
    'cricket',
    'london',
    'india',
    'erp',
];
/** Letters that stand in for others: p@ssw0rd, s3cret, l3tm31n. */
const LOOK_ALIKES = {
    '0': 'o',
    '1': 'l',
    '3': 'e',
    '4': 'a',
    '5': 's',
    '7': 't',
    '@': 'a',
    $: 's',
    '!': 'i',
};
const RUNS = [
    'abcdefghijklmnopqrstuvwxyz',
    'qwertyuiopasdfghjklzxcvbnm',
    'zyxwvutsrqponmlkjihgfedcba',
];
/**
 * The password as a run of lower-case letters. With `undoLookAlikes`, digits and symbols that stand in for
 * letters are turned back (p@ssw0rd -> password); without it they are dropped (Password1234 -> password).
 * Both readings are checked, because a trailing "1234" would otherwise be misread as letters.
 */
function letterBase(password, undoLookAlikes) {
    return [...password.toLowerCase()]
        .map((c) => (undoLookAlikes ? (LOOK_ALIKES[c] ?? c) : c))
        .filter((c) => c >= 'a' && c <= 'z')
        .join('');
}
/** Whether the text is made up entirely of common words (so "welcomeschool" and "passwordpassword" count). */
function madeOfCommonWords(text) {
    const reach = new Array(text.length + 1).fill(false);
    reach[0] = true;
    for (let i = 0; i < text.length; i += 1) {
        if (!reach[i])
            continue;
        for (const word of COMMON_WORDS)
            if (text.startsWith(word, i))
                reach[i + word.length] = true;
    }
    return reach[text.length];
}
/** Why the password is too easy to guess, or null when nothing is wrong with it. */
export function weakPasswordReason(password, context = {}) {
    const lower = password.toLowerCase();
    if (new Set(lower).size <= 3)
        return 'Password repeats too few different characters';
    const plain = letterBase(password, false);
    // A look-alike inside the word (p@ssw0rd) is undone; digits and symbols hung on the end (…2024!) are not.
    const trimmed = password.replace(/^[^a-z]+|[^a-z]+$/gi, '');
    const bases = [...new Set([plain, letterBase(password, true), letterBase(trimmed, true)])];
    if (plain.length > 0 && plain.length < 3 && new Set(lower).size < 6)
        return 'Password is too simple';
    if (bases.some((base) => base.length >= 4 && madeOfCommonWords(base)))
        return 'Password is a common word or phrase; choose something less guessable';
    if (bases.some((base) => base.length >= 6 && RUNS.some((run) => run.includes(base))))
        return 'Password is a keyboard or alphabet run';
    const digits = lower.replace(/\D/g, '');
    // Mostly a counting sequence: "123456789012" with a letter added is still 123456789012.
    if (digits.length >= 8 && '01234567890123456789'.includes(digits) && plain.length <= 2)
        return 'Password is mostly a counting sequence';
    const parts = new Set();
    const add = (value) => {
        for (const part of (value ?? '').toLowerCase().split(/[^a-z0-9]+/))
            if (part.length >= 4)
                parts.add(part);
    };
    add(context.email?.split('@')[0]);
    add(context.username);
    add(context.fullName);
    for (const part of parts)
        if (lower.includes(part))
            return 'Password must not contain your name, username or e-mail';
    return null;
}
//# sourceMappingURL=password-strength.js.map